import { DvIcon } from '../DvIcon.js';
import { getSchema, type Field } from '../../lib/entitySchemas.js';

type FM = Record<string, unknown>;

function str(fm: FM, k: string): string {
  const v = fm[k];
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}
function num(fm: FM, k: string): number | null {
  const v = fm[k];
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v);
  return null;
}
function arr(fm: FM, k: string): string[] {
  const v = fm[k];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === 'string') return v.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  return [];
}

export interface Feat { name: string; ftype: string; text: string; }
function feats(fm: FM): Feat[] {
  const v = fm.features;
  if (!Array.isArray(v)) return [];
  return v.filter((f): f is Feat => !!f && typeof f === 'object' && typeof (f as Feat).name === 'string' && (f as Feat).name.trim().length > 0);
}

/** Статичний піп-бар — порожні слоти = ємність ресурсу (як у прототипі, advHp=0). */
function Pips({ n, empty }: { n: number; empty: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {Array.from({ length: Math.max(0, Math.min(20, n)) }, (_, i) => (
        <span key={i} style={{ width: 15, height: 15, borderRadius: 4, border: `1.6px solid ${empty}`, background: 'transparent' }} aria-hidden="true" />
      ))}
    </div>
  );
}

export interface StatCardProps {
  type: string;
  name: string;
  epithet?: string;
  native?: string;
  fm: FM;
  isGm: boolean;
  /** Режим конструктора: показувати скелет усіх полів типу навіть без даних. */
  preview?: boolean;
}

export function StatCard({ type, name, epithet, native, fm, isGm, preview = false }: StatCardProps): JSX.Element | null {
  const schema = getSchema(type);
  if (!schema) return null;
  if (type === 'adversary') return <AdversaryCard name={name} native={native} fm={fm} />;
  return <LightCard schema={schema} name={name} epithet={epithet} native={native} fm={fm} isGm={isGm} preview={preview} />;
}

/* ───────────────── Ворог — самодостатній пергамент (1:1 з прототипом) ───────────────── */
function AdversaryCard({ name, native, fm }: { name: string; native?: string; fm: FM }): JSX.Element {
  const P = { bg: '#F8EFD8', sunken: '#ECDDBC', line: '#D9C399', line2: '#C7AA76', ink: '#3A3022', muted: '#6F5F44', brick: '#9A3D2E' };
  const tier = str(fm, 'tier') || '1';
  const tierColor = ['var(--tier-1)', 'var(--tier-2)', 'var(--tier-3)', 'var(--tier-4)'][Math.max(0, Math.min(3, Number(tier) - 1))];
  const advType = str(fm, 'advType');
  const creature = str(fm, 'creature');
  const motives = str(fm, 'motives');
  const diff = str(fm, 'difficulty');
  const thr = (str(fm, 'thrMajor') || '—') + ' / ' + (str(fm, 'thrSevere') || '—');
  const atk = str(fm, 'atk');
  const hp = num(fm, 'hp') ?? 6;
  const stress = num(fm, 'stress') ?? 3;
  const attack = str(fm, 'attack');
  const exps = arr(fm, 'experiences');
  const fl = feats(fm);
  const lbl = (t: string): JSX.Element => <div style={{ font: '500 9px "Spline Sans Mono", monospace', letterSpacing: '0.1em', textTransform: 'uppercase', color: P.muted }}>{t}</div>;
  const statNum = (v: string): JSX.Element => <span style={{ font: '600 20px "Spline Sans Mono", monospace' }}>{v || '—'}</span>;
  return (
    <div style={{ borderRadius: 12, background: P.bg, border: `1.5px solid ${P.line}`, color: P.ink, boxShadow: '0 20px 44px -26px rgba(58,48,34,0.55)', overflow: 'hidden', clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))' }}>
      <div style={{ height: 6, background: P.brick }} />
      <div style={{ padding: '15px 17px 13px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <span style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EEDFC0', border: `1px solid ${P.line2}` }}>
            <DvIcon name="beast" size={24} color={P.brick} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '600 24px "Cormorant Garamond", serif', lineHeight: 1.05, color: P.ink }}>{name}</div>
            {native && <div style={{ font: 'italic 400 13px Spectral, serif', color: P.muted, marginTop: 1 }}>{native}{creature ? ` · ${creature}` : ''}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 11, flexWrap: 'wrap' }}>
          <span style={{ font: '600 10px "Spline Sans Mono", monospace', color: '#fff', background: tierColor, padding: '3px 9px', borderRadius: 6 }}>TIER {tier}</span>
          {advType && <span style={{ font: '600 10px "Spline Sans Mono", monospace', color: P.brick, background: '#F1DFC2', border: `1px solid ${P.line2}`, padding: '3px 9px', borderRadius: 6 }}>{advType.toUpperCase()}</span>}
          {creature && <span style={{ font: '500 10px "Spline Sans Mono", monospace', color: P.muted, border: `1px solid ${P.line2}`, padding: '3px 9px', borderRadius: 6 }}>{creature}</span>}
        </div>
        {motives && <div style={{ font: 'italic 400 13px Spectral, serif', color: P.muted, marginTop: 10, lineHeight: 1.5 }}>Мотиви й тактика: {motives}</div>}
      </div>
      <div style={{ margin: '0 13px', background: P.sunken, border: `1px solid ${P.line2}`, borderRadius: 9, padding: '12px 13px' }}>
        <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
          <div>{lbl('Складність')}<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}><DvIcon name="difficulty" size={15} color={P.brick} />{statNum(diff)}</div></div>
          <div>{lbl('Пороги М/Т')}<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}><DvIcon name="threshold" size={15} color={P.brick} />{statNum(thr)}</div></div>
          <div>{lbl('Атака')}<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}><DvIcon name="attack" size={15} color={P.brick} />{statNum(atk ? (Number(atk) >= 0 ? `+${atk}` : atk) : '')}</div></div>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <div><div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}><DvIcon name="hp" size={13} color={P.brick} />{lbl('HP')}</div><Pips n={hp} empty={P.line2} /></div>
          <div><div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}><DvIcon name="stress" size={13} color="#6E5C9A" />{lbl('Стрес')}</div><Pips n={stress} empty={P.line2} /></div>
        </div>
      </div>
      {attack && (
        <div style={{ margin: '11px 15px 0', font: '400 13.5px Spectral, serif', color: P.ink, display: 'flex', alignItems: 'center', gap: 7 }}>
          <DvIcon name="attack" size={15} color={P.muted} /><span>{attack}</span>
        </div>
      )}
      {exps.length > 0 && (
        <div style={{ margin: '10px 15px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {exps.map((e, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: '500 12px Spectral, serif', color: '#2f6e3c', background: '#E4F0DC', border: '1px solid #C3DEB2', padding: '2px 10px', borderRadius: 999 }}>
              <DvIcon name="leaf" size={13} color="#4f8a4a" />{e}
            </span>
          ))}
        </div>
      )}
      {fl.length > 0 && (
        <div style={{ margin: '15px 15px 6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            <span style={{ font: '600 11px "Spline Sans Mono", monospace', letterSpacing: '0.16em', color: P.brick }}>ОСОБЛИВОСТІ</span>
            <span style={{ flex: 1, height: 1, background: P.line2 }} />
          </div>
          {fl.map((f, i) => <ParchmentFeat key={i} f={f} line={P.line2} ink={P.ink} />)}
        </div>
      )}
      <div style={{ height: 14 }} />
    </div>
  );
}

const PARCH_FEAT: Record<string, [string, string]> = { 'Пасивна': ['#6F5F44', '#E7D6B8'], 'Дія': ['#2E6FA8', '#DCE7F0'], 'Реакція': ['#B07B22', '#F2E6C6'] };
function ParchmentFeat({ f, line, ink }: { f: Feat; line: string; ink: string }): JSX.Element {
  const c = PARCH_FEAT[f.ftype] ?? ['#6F5F44', '#E7D6B8'];
  return (
    <div style={{ paddingTop: 11, marginTop: 11, borderTop: `1px dashed ${line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ font: '600 italic 16px "Cormorant Garamond", serif', color: ink }}>{f.name}</span>
        {f.ftype && <span style={{ font: '600 9px "Spline Sans Mono", monospace', letterSpacing: '0.04em', color: c[0], background: c[1], border: `1px solid ${c[0]}44`, padding: '1px 7px', borderRadius: 5 }}>{f.ftype.toUpperCase()}</span>}
      </div>
      {f.text && <div style={{ font: '400 13.5px Spectral, serif', lineHeight: 1.55, color: ink }}>{f.text}</div>}
    </div>
  );
}

/* ───────────────── Загальна картка (data-driven зі схеми) ───────────────── */
function LightCard({ schema, name, epithet, native, fm, isGm, preview }: {
  schema: NonNullable<ReturnType<typeof getSchema>>; name: string; epithet?: string; native?: string; fm: FM; isGm: boolean; preview?: boolean;
}): JSX.Element {
  const accent = `var(--${schema.cat})`;
  const has = (f: Field): boolean => {
    if (preview) return true; // у конструкторі показуємо скелет усіх полів
    if (f.kind === 'tags') return arr(fm, f.key).length > 0;
    return str(fm, f.key).trim().length > 0;
  };
  const dash = (v: string): string => (v.trim() ? v : '—');
  const metaChips = schema.fields.filter((f) => f.group === 'meta' && (f.kind === 'enum' || f.kind === 'text' || f.kind === 'int') && has(f));
  const statRows = schema.fields.filter((f) => f.group === 'stat' && ['text', 'int', 'enum', 'signed'].includes(f.kind) && has(f));
  const pipRows = schema.fields.filter((f) => f.kind === 'pips' && (preview || num(fm, f.key) !== null));
  const tagGroups = schema.fields.filter((f) => f.kind === 'tags' && f.group === 'stat' && has(f));
  const social = schema.fields.filter((f) => f.group === 'social' && !f.gm && has(f));
  const gmRows = schema.fields.filter((f) => f.gm && has(f));
  const fl = feats(fm);

  return (
    <div className="dv-statcard">
      <div className="dv-statcard__stripe" style={{ background: accent }} />
      <div className="dv-statcard__head">
        <span className="dv-statcard__ico" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)`, color: accent }}>
          <DvIcon name={schema.icon} size={24} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="dv-statcard__name">{name}</div>
          {native && <div className="dv-statcard__native">{native}</div>}
          {epithet && <div className="dv-statcard__epithet">{epithet}</div>}
        </div>
      </div>
      {metaChips.length > 0 && (
        <div className="dv-statcard__chips">
          {metaChips.map((f) => {
            const v = str(fm, f.key);
            const isTier = f.key === 'tier';
            const chipText = v ? (isTier ? `T${v}` : v) : f.label;
            return <span key={f.key} className={`dv-chip ${isTier && v ? `dv-chip--tier${v}` : ''}`} style={!v ? { opacity: 0.5 } : undefined}>{chipText}</span>;
          })}
        </div>
      )}
      {statRows.length > 0 && (
        <div className="dv-statcard__strip">
          {statRows.map((f) => {
            const v = str(fm, f.key);
            const out = f.kind === 'signed' && v && Number(v) >= 0 ? `+${v}` : dash(v);
            return <div key={f.key}><div className="dv-stat__k">{f.label}</div><div className="dv-stat__v">{out}</div></div>;
          })}
        </div>
      )}
      {pipRows.map((f) => (
        <div key={f.key} className="dv-statcard__piprow">
          <div className="dv-stat__k">{f.label}</div>
          <Pips n={num(fm, f.key) ?? f.max ?? 6} empty="var(--dv-line-strong)" />
        </div>
      ))}
      {tagGroups.map((f) => (
        <div key={f.key} className="dv-statcard__tags">
          <div className="dv-stat__k">{f.label}</div>
          <div className="dv-statcard__tagrow">
            {arr(fm, f.key).length > 0
              ? arr(fm, f.key).map((t, i) => <span key={i} className="dv-statcard__tag">{t}</span>)
              : <span className="dv-statcard__tag" style={{ opacity: 0.5 }}>—</span>}
          </div>
        </div>
      ))}
      {social.length > 0 && (
        <div className="dv-statcard__social">
          <div className="dv-stat__k">Соціальний блок</div>
          {social.map((f) => <div key={f.key} className="dv-statcard__socrow"><b>{f.label}: </b>{dash(str(fm, f.key))}</div>)}
        </div>
      )}
      {fl.length > 0 && (
        <div className="dv-statcard__feats">
          <div className="dv-statcard__featshead"><span style={{ color: accent }}>ОСОБЛИВОСТІ</span><span className="dv-statcard__featsrule" /></div>
          {fl.map((f, i) => <LightFeat key={i} f={f} />)}
        </div>
      )}
      {isGm && gmRows.length > 0 && (
        <div className="dv-gmnote">
          <div className="dv-gmnote__label"><DvIcon name="gmeye" size={14} /> Лише Майстер</div>
          {gmRows.map((f) => <div key={f.key} className="dv-statcard__socrow"><b>{f.label}: </b>{dash(str(fm, f.key))}</div>)}
        </div>
      )}
    </div>
  );
}

function LightFeat({ f }: { f: Feat }): JSX.Element {
  const cls = f.ftype === 'Дія' ? 'action' : f.ftype === 'Реакція' ? 'reaction' : 'passive';
  return (
    <div className="dv-feat">
      <div className="dv-feat__top">
        <span className="dv-feat__name">{f.name}</span>
        {f.ftype && <span className={`dv-feat__tag dv-feat__tag--${cls}`}>{f.ftype.toUpperCase()}</span>}
      </div>
      {f.text && <div className="dv-feat__text">{f.text}</div>}
    </div>
  );
}

/** Чи має сенс показувати стат-картку для цього типу/frontmatter. */
export function hasStatData(type: string, fm: FM): boolean {
  const schema = getSchema(type);
  if (!schema) return false;
  if (type === 'adversary') return true;
  return schema.fields.some((f) => {
    if (f.kind === 'tags') return arr(fm, f.key).length > 0;
    if (f.kind === 'pips') return num(fm, f.key) !== null;
    return str(fm, f.key).trim().length > 0;
  });
}
